// utils/validationUtils.ts

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean
  correctedQuantity?: number
  error?: string
}

/**
 * Round quantity to valid step size with proper precision handling
 */
export const roundToStepSize = (quantity: number, stepSize: number = 0.001): number => {
  if (stepSize === 0) return quantity
  
  // Determine precision from step size
  const precision = stepSize >= 1 ? 0 : 
                   stepSize >= 0.1 ? 1 :
                   stepSize >= 0.01 ? 2 :
                   stepSize >= 0.001 ? 3 :
                   stepSize >= 0.0001 ? 4 :
                   stepSize >= 0.00001 ? 5 : 8
  
  // Use proper rounding to handle floating point precision issues
  // Always round down to ensure we don't exceed limits
  const steps = Math.floor(quantity / stepSize)
  const rounded = steps * stepSize
  
  return parseFloat(rounded.toFixed(precision))
}

/**
 * Get trading rules for a given symbol
 * Returns step size and minimum quantity based on common Binance patterns
 */
export const getTradingRules = (symbol: string) => {
  const upperSymbol = symbol.toUpperCase()
  
  // Default rules
  let stepSize = 0.001
  let minQty = 0.001
  
  // Special cases for popular pairs
  if (upperSymbol.includes('BTC')) {
    stepSize = 0.00001
    minQty = 0.00001
  } else if (upperSymbol.includes('ETH')) {
    stepSize = 0.0001
    minQty = 0.0001
  } else if (upperSymbol.includes('BNB')) {
    stepSize = 0.001
    minQty = 0.001
  }
  
  return { stepSize, minQty }
}

/**
 * Validate quantity against trading rules
 */
export const validateQuantity = (quantity: number, symbol: string, baseCurrency: string): ValidationResult => {
  const { stepSize, minQty } = getTradingRules(symbol)
  
  // Check minimum quantity
  if (quantity < minQty) {
    return {
      isValid: false,
      error: `Minimum quantity is ${minQty} ${baseCurrency}`
    }
  }
  
  // Round to step size
  const correctedQuantity = roundToStepSize(quantity, stepSize)
  
  // Check if correction was needed (use a very small tolerance for floating point comparison)
  const tolerance = stepSize * 0.01  // Small tolerance for floating point precision
  if (Math.abs(quantity - correctedQuantity) > tolerance) {
    return {
      isValid: false,
      correctedQuantity,
      error: `Quantity must be a multiple of ${stepSize}. Suggested: ${correctedQuantity}`
    }
  }
  
  return { isValid: true, correctedQuantity }
}

/**
 * Parse currency pair into base and quote currencies
 */
export const parseCurrencyPair = (symbol: string) => {
  const upperSymbol = symbol.toUpperCase()
  // Common quote currencies in order of priority
  const quoteAssets = ['USDT', 'USDC', 'BUSD', 'BTC', 'ETH', 'BNB']
  
  for (const quote of quoteAssets) {
    if (upperSymbol.endsWith(quote)) {
      return {
        baseCurrency: upperSymbol.slice(0, -quote.length),
        quoteCurrency: quote
      }
    }
  }
  
  // Fallback: assume last 4 characters are quote currency
  return {
    baseCurrency: upperSymbol.slice(0, -4),
    quoteCurrency: upperSymbol.slice(-4)
  }
}